/**
 * Queue Datasource (Infrastructure)
 *
 * Handles raw API communication for queue operations.
 * Similar to Flutter's datasource pattern.
 *
 * This layer is responsible for:
 * - Making HTTP requests
 * - Parsing raw responses
 * - Throwing network exceptions
 */

import { get, post, put } from '@/shared/api';

import { v2CallResponseToQueueModel } from '../../application/mappers/v2CallResponseToQueueModel';

import type { V2CallResponse } from '@/shared/services/socketio/types';
import type {
  CallQueuePaginatedRequest,
  CallQueuePaginatedResponse,
  ReorderRequest,
  MoveQueueResponse,
  CallQueueDeleteRequest,
  DeleteQueueResponse,
  CaseAssignCallQueueRequest,
  MessageResponse,
  CallDetailResponse,
  SubmitCallDetailRequest,
  CallControlAction,
  ContactSearchResult,
  CaseSearchResult,
  ForwardCallRequest,
} from '../../domain/types';

// ============================================================================
// Datasource Interface
// ============================================================================

export interface QueueDatasource {
  getQueueList(request: CallQueuePaginatedRequest): Promise<CallQueuePaginatedResponse>;
  moveQueue(request: ReorderRequest): Promise<MoveQueueResponse>;
  deleteQueues(requests: CallQueueDeleteRequest[]): Promise<DeleteQueueResponse>;
  bulkAddCasesToQueue(request: CaseAssignCallQueueRequest): Promise<MessageResponse>;
  getCallDetail(callId: number): Promise<CallDetailResponse>;
  submitCallDetail(callId: number, data: SubmitCallDetailRequest): Promise<void>;
  controlCall(action: CallControlAction): Promise<MessageResponse>;
  makeNextCall(): Promise<MessageResponse>;
  searchContacts(callId: number, query: string): Promise<ContactSearchResult[]>;
  searchCasesForContact(callId: number, contactId: number): Promise<CaseSearchResult[]>;
  forwardCall(request: ForwardCallRequest): Promise<MessageResponse>;
  cancelForwardCall(): Promise<MessageResponse>;
}

// ============================================================================
// Datasource Implementation
// ============================================================================

export class QueueDatasourceImpl implements QueueDatasource {
  async getQueueList(request: CallQueuePaginatedRequest): Promise<CallQueuePaginatedResponse> {
    const apiRequest = {
      page: request.page,
      per_page: request.per_page,
      search_text: request.search_text ?? null,
      include_ended: request.include_ended,
      all_queue_calls: request.all_call_queues,
    };

    // v2 API returns V2CallResponse items — map to QueueModel
    const response = await post<{ queues: V2CallResponse[]; page: number; pages: number; total: number }>(
      '/app/queue/paginated',
      apiRequest
    );

    return {
      queues: response.queues.map(v2CallResponseToQueueModel),
      page: response.page,
      pages: response.pages,
      total: response.total,
    };
  }

  async moveQueue(request: ReorderRequest): Promise<MoveQueueResponse> {
    const apiRequest = {
      queue_and_order: request.queue_and_order,
      paginated: request.paginated ?? null,
    };

    const response = await post<MoveQueueResponse>('/queue/move', apiRequest);
    return response;
  }

  async deleteQueues(requests: CallQueueDeleteRequest[]): Promise<DeleteQueueResponse> {
    // Backend expects { call_ids: int[] } at PUT /app/queue/cancel
    const apiRequest = {
      call_ids: requests.map(req => req.id),
    };

    const response = await put<{ message: string }>('/app/queue/cancel', apiRequest);
    return { success: true, message: response.message };
  }

  async bulkAddCasesToQueue(request: CaseAssignCallQueueRequest): Promise<MessageResponse> {
    // Transform frontend request shape to backend BulkAddToQueueRequest
    // Backend expects: { case_ids: int[], user_id?: int, priority: bool }
    const apiRequest = {
      case_ids: request.call_queue_requests.map(r => r.case_id),
      user_id: request.call_queue_requests[0]?.user_id,
      priority: request.priority,
    };
    const response = await post<MessageResponse>('/app/queue/add', apiRequest);
    return response;
  }

  async getCallDetail(callId: number): Promise<CallDetailResponse> {
    const response = await get<CallDetailResponse>(`/app/call/${callId}/detail`);
    return response;
  }

  async submitCallDetail(callId: number, data: SubmitCallDetailRequest): Promise<void> {
    await post(`/app/call/${callId}/detail`, data);
  }

  async controlCall(action: CallControlAction): Promise<MessageResponse> {
    const response = await post<MessageResponse>('/app/call/control', { action });
    return response;
  }

  async makeNextCall(): Promise<MessageResponse> {
    const response = await post<MessageResponse>('/app/queue/next-call');
    return response;
  }

  async searchContacts(callId: number, query: string): Promise<ContactSearchResult[]> {
    const response = await get<ContactSearchResult[]>(
      `/app/call/${callId}/contacts?query=${encodeURIComponent(query)}`
    );
    return response;
  }

  async searchCasesForContact(callId: number, contactId: number): Promise<CaseSearchResult[]> {
    const response = await get<CaseSearchResult[]>(
      `/app/call/${callId}/contact/${contactId}/cases`
    );
    return response;
  }

  async forwardCall(request: ForwardCallRequest): Promise<MessageResponse> {
    const response = await post<MessageResponse>('/app/call/forward/initiate', request);
    return response;
  }

  async cancelForwardCall(): Promise<MessageResponse> {
    const response = await post<MessageResponse>('/app/call/forward/cancel');
    return response;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const queueDatasource: QueueDatasource = new QueueDatasourceImpl();
